import PropTypes from 'prop-types';
import React, { Fragment, useEffect, useState } from 'react';
import { pure, compose } from 'recompose';
import fullName, { invertedFullName } from 'common/utils/fullName';
import {
    Item,
    Initials,
    InitialsErrorBadge,
    Date,
    connectActiveItem,
} from 'components/list';
import { withSession } from '../../../../components/sessions';
import { POST } from '../../../../utils/httpMethods';
import './ListItem.scss';

const statusLabels = {
    pending: 'En attente',
    approved: 'Validé',
    rejected: 'Invalidé',
};

const getTypeLabel = (type) => ('LODEOMCIF' === type ? 'CIF LODEOM' : 'CIF SCPI');

const ListItem = ({
    item,
    session,
    allMode,
}) => {
    const {
        id,
        ripId,
        name,
        type,
        target,
        status,
        requestedAt,
        idEnvelopeESigne,
        caseManager,
        user,
        firstName,
        lastName,
        errorCount,
        signatureDate,
    } = item;

    // statut stocké en base en attendant la réponse de l'api eSign
    const [esignStatus, setEsignStatus] = useState(null);
    const [isEsignUnavailable, setIsEsignUnavailable] = useState(false);
    const isApproved = 'approved' === status;

    useEffect(() => {
        if (!isApproved || !idEnvelopeESigne) {
            return;
        }

        // la route GetStatus interroge l'api eSign (getStatusESigne) et met à jour statusEnvelope de cette préconisation
        POST(`/api/contracts/${ripId}/eSigne/envelopes/GetStatus/${idEnvelopeESigne}`, {
            body: { session, target, productType: type },
        })
            .then((response) => response.json())
            .then(({ shortStatusEsigne }) => shortStatusEsigne && setEsignStatus(shortStatusEsigne))
            .catch((error) => {
                // plateforme eSign injoignable ou en erreur : le statut stocké n'est pas fiable
                console.error('Erreur GetStatus eSign :', error);
                setIsEsignUnavailable(true);
            });
    }, [ripId, idEnvelopeESigne, isApproved]);

    return (
        <Fragment>
            <Item
                key={id}
            >
                {allMode && <td>{user ? invertedFullName(user) : '-'}</td>}
                <Initials firstName={firstName} lastName={lastName}>
                    {null !== errorCount && (
                        <InitialsErrorBadge
                            errorCount={errorCount}
                            signatureDate={signatureDate}
                            isAvailable
                        />
                    )}
                </Initials>
                <td>{name}</td>
                <td>{getTypeLabel(type)}</td>
                {requestedAt ? <Date date={requestedAt} /> : <td>-</td>}
                <td>
                    <span className={`cif-status-tag cif-status-tag--${status}`}>{statusLabels[status]}</span>
                    {isApproved && (esignStatus || isEsignUnavailable) && (
                        <div className="cif-status-esign">
                            <span>Suivi signature :</span>
                            <span className={`cif-status-esign--esign${isEsignUnavailable ? ' cif-status-esign--unavailable' : ''}`}>
                                {isEsignUnavailable ? 'Indisponible' : esignStatus}
                            </span>
                        </div>
                    )}
                </td>
                {/* NC : la date du statut n'est pas encore stockée */}
                {/* <td>NC</td> */}
                <td>{caseManager ? fullName(caseManager) : '-'}</td>
            </Item>
        </Fragment>
    );
};

ListItem.propTypes = {
    item: PropTypes.shape({
        id: PropTypes.string,
        ripId: PropTypes.string,
        name: PropTypes.string,
        type: PropTypes.string,
        target: PropTypes.string,
        status: PropTypes.oneOf(Object.keys(statusLabels)),
        requestedAt: PropTypes.string,
        idEnvelopeESigne: PropTypes.string,
        caseManager: PropTypes.shape({
            firstName: PropTypes.string,
            lastName: PropTypes.string,
        }),
        user: PropTypes.shape({
            firstName: PropTypes.string,
            lastName: PropTypes.string,
        }),
        firstName: PropTypes.string,
        lastName: PropTypes.string,
        errorCount: PropTypes.number,
        signatureDate: PropTypes.string,
    }).isRequired,
    session: PropTypes.shape({
        status: PropTypes.string,
    }).isRequired,
    allMode: PropTypes.bool,
};

ListItem.defaultProps = {
    allMode: false,
};

export default compose(
    withSession,
    pure,
    connectActiveItem(({ item: { id } }) => id, (item, { activeItem, target }) => {
        const isActive = item === activeItem;

        return {
            isActive,
            contactDisplayed: isActive && 'contact' === target,
        };
    }),
)(ListItem);
